import { useCallback, useEffect, useRef, useState } from 'react';
import { useWorkspaceContext } from 'src/providers/WorkspaceProvider/Workspace.context';
import { LeadCloseService } from './LeadClose.service';
import type { tCloseFeedRow } from './LeadClose.types';
import { useLeadClose } from './useLeadClose.hook';

/** The Historial rows this module draws: a result's registration, edit or deletion, and the old-amount answer. */
const RESULT_FIELDS = new Set([
  'close_sold',
  'close_lost',
  'close_resolved',
  'close_prior_amount',
]);

/** Pages read in one go when Historial opens; "Cargar más" goes past it. */
const MAX_PAGES = 20;

export type tLeadCloseFeed = {
  rows: tCloseFeedRow[];
  loading: boolean;
  failed: boolean;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  reload: () => void;
};

/**
 * The lead's result rows of Historial (U15), one page at a time, only while
 * Historial is open (`leadId` null reads nothing). A page that answers after
 * the lead changed is dropped; an edit or deletion anywhere reads it again.
 */
export function useLeadCloseFeed(leadId: string | null): tLeadCloseFeed {
  const { currentWorkspace } = useWorkspaceContext();
  const workspaceId = currentWorkspace?.id ?? '';
  const { historyVersion } = useLeadClose();
  const [rows, setRows] = useState<tCloseFeedRow[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [trigger, setTrigger] = useState(0);
  const asking = useRef<string | null>(null);

  // A new lead starts empty; a reload of the same lead keeps its rows on
  // screen until the new ones arrive, so nothing flashes after an edit.
  const shownLead = useRef<string | null>(null);

  useEffect(() => {
    const key = `${workspaceId}:${leadId}`;
    if (shownLead.current !== key) {
      shownLead.current = key;
      setRows([]);
      setCursor(null);
    }
    setFailed(false);
    if (!leadId || !workspaceId) return;
    const token = `${key}:${historyVersion}:${trigger}`;
    asking.current = token;
    setLoading(true);
    // Every page while Historial is open: the feed pages over all of the
    // lead's changes and keeps only results, so one page may hold none of
    // them. Bounded, on demand, never while Historial is closed.
    (async () => {
      const collected: tCloseFeedRow[] = [];
      let next: string | undefined;
      let pages = 0;
      try {
        do {
          const page = await LeadCloseService.feed(workspaceId, leadId, next);
          if (asking.current !== token) return;
          collected.push(
            ...page.rows.filter((row) => RESULT_FIELDS.has(row.field)),
          );
          next = page.next_cursor ?? undefined;
          pages += 1;
        } while (next && pages < MAX_PAGES);
        setRows(collected);
        setCursor(next ?? null);
      } catch {
        if (asking.current === token) setFailed(true);
      } finally {
        if (asking.current === token) setLoading(false);
      }
    })();
  }, [leadId, workspaceId, historyVersion, trigger]);

  const loadMore = useCallback(async () => {
    if (!leadId || !cursor || loading) return;
    const token = asking.current;
    setLoading(true);
    try {
      const page = await LeadCloseService.feed(workspaceId, leadId, cursor);
      if (asking.current !== token) return;
      setRows((held) => [
        ...held,
        ...page.rows.filter((row) => RESULT_FIELDS.has(row.field)),
      ]);
      setCursor(page.next_cursor);
    } catch {
      if (asking.current === token) setFailed(true);
    } finally {
      if (asking.current === token) setLoading(false);
    }
  }, [leadId, cursor, loading, workspaceId]);

  const reload = useCallback(() => setTrigger((value) => value + 1), []);

  return { rows, loading, failed, hasMore: Boolean(cursor), loadMore, reload };
}
